import { useRef } from 'react'
import { createPortal } from 'react-dom'
import toast from 'react-hot-toast'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import Stack from '@mui/material/Stack'
import DialogTitle from '@components/_mui/DialogTitle'
import Typography from '@components/_mui/Typography'

import { FileRelatedUser } from '@components/FileUploader'

import { StagedTile, tileGridStyles } from './AttachmentTile'
import DragOverlay from './DragOverlay'
import DropStrip from './DropStrip'
import QueueHint from './QueueHint'
import { useFileIntake } from './TaskAttachments.hooks'

export default function UploadDialog({ task, queue }) {
  const open = queue.items.length > 0

  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={queue.uploading ? undefined : queue.clear} scroll="paper">
      {open && <Content task={task} queue={queue} />}
    </Dialog>
  )
}

function Content({ task, queue }) {
  const rootRef = useRef(null)
  const { dragging, scope } = useFileIntake({ rootRef, onFiles: queue.add, disabled: queue.uploading })

  const handleUpload = () =>
    queue.upload(task).then(({ uploaded, failed }) => {
      if (failed === 0 && uploaded > 0) toast.success(uploaded > 1 ? `${uploaded} files were uploaded` : 'File was uploaded')
    })

  return (
    <>
      <DialogTitle onClose={queue.uploading ? undefined : queue.clear}>Upload files</DialogTitle>
      <DialogContent>
        <Stack ref={rootRef} spacing={2}>
          {task.patient && <FileRelatedUser user={task.patient} />}
          <DropStrip onFiles={queue.add} disabled={queue.uploading} text="Drop or paste more files here, or" />
          <Box sx={tileGridStyles}>
            {queue.items.map((item) => (
              <StagedTile
                key={item.id}
                item={item}
                onPreviewed={() => queue.markPreviewed(item.id)}
                onRemove={queue.uploading ? undefined : () => queue.remove(item.id)}
                onRetry={item.status === 'error' ? () => queue.retry(item.id) : undefined}
                onNameChange={(name) => queue.setName(item.id, name)}
              />
            ))}
          </Box>
          <Typography sx={{ color: 'text.secondary' }}>
            <QueueHint queue={queue} />
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" disabled={queue.uploading} onClick={queue.clear}>
          Cancel
        </Button>
        <Button variant="contained" disabled={!queue.allPreviewed} loading={queue.uploading} onClick={handleUpload}>
          Upload {queue.ready.length > 1 ? `${queue.ready.length} files` : 'file'}
        </Button>
      </DialogActions>
      {dragging && scope && createPortal(<DragOverlay identifier={task.identifier} />, scope)}
    </>
  )
}
